import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchWithPublicMode, getPublicModeParams } from '../utils/apiUtils';
import { ReliabilityPanel, summarizeReliability } from './reliability/ReliabilityDialog';
import { ReliabilityDescription, reliabilityApi } from '../services/reliabilityApi';
import { FunctionLearningAccordion } from './learning/FunctionLearningAccordion';
import { PlatformRouteAccordion } from './platform/PlatformRouteAccordion';
import { RouteDraft, slugProblem } from './platform/PlatformRouteCard';
import { functionNameFor, platformRoutesApi } from '../services/platformRoutesApi';
import { useLearningStatus } from './learning/common';
import { learningPath } from '../services/learningApi';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Snackbar,
  Paper,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  Switch,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as TestIcon,
  AutoAwesome as AIIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  ShowChart as ShowChartIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Clear as ClearIcon,
  Code as CodeIcon,
  ViewList as LibraryIcon,
} from '@mui/icons-material';
import { ObjectBuilderComponent } from './ObjectBuilderComponent';
import { FunctionTester } from './FunctionTester';
import { compileFunctionDataForImprovement, improveWithAI, saveGeneratedReturnObject } from '../utils/aiFunctionImprover';
import { formatAsPydanticClass } from '../utils/pydanticFormatter';
import { JsonSchema, objectToJsonSchema } from '../utils/jsonSchema';
import { ObjectSchemaView } from './schema/ObjectSchemaView';
import { CodeHighlight } from './CodeHighlight';
import { CreateWithAIHero } from './function-builder/CreateWithAIHero';
import { SetUpYourselfBar } from './function-builder/SetUpYourselfBar';
import { ChangeWithAIBar } from './function-builder/ChangeWithAIBar';
import { DraftReviewNotice } from './function-builder/DraftReviewNotice';
import { SavedNextSteps } from './common/SavedNextSteps';
import { UseCasesModal } from './UseCasesModal';
import { DEFAULT_ALLOWED_MODELS, getAllModelOptions } from '../config/modelConfig';
import { ModelSelector } from './ModelSelector';
import { transformObjectWithNestedFields, mapFieldForPydantic } from '../utils/fieldTransformers';

interface CustomObject {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  fields?: any[];
  field_count?: number;
  customer_id?: string;  // Optional until migration is complete
}

interface AIFunction {
  id?: string;
  original_uuid?: string;
  name: string;
  display_name: string;
  description: string;
  instructions: string;
  input_types: string[];
  allowed_models: string[];
  response_format?: any;
  response_type: 'text' | 'object' | 'list' | 'image' | 'video' | 'audio';
  response_object_id?: string | null;
  is_list_response?: boolean;
  version?: number;
  created_at?: string;
  is_public?: boolean;
  updated_at?: string;
  metadata?: any;
}

interface TestMaterial {
  id: string;
  name: string;
  description?: string;
  data: {
    content_type: string;
    content: string;
    file_key?: string;
    file_name?: string;
    file_size?: number;
  };
}

const inputTypeOptions = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'json', label: 'JSON', icon: '{}' },
  { value: 'image', label: 'Image', icon: '🖼️' },
  { value: 'audio', label: 'Audio', icon: '🎵' },
  { value: 'video', label: 'Video', icon: '🎥' },
  { value: 'pdf', label: 'PDF', icon: '📄' },
  { value: 'binary', label: 'File (Any Type)', icon: '📎' },
];

const modelOptions = getAllModelOptions();

const toInputType = (value: string | null) =>
  value && inputTypeOptions.some((t) => t.value === value) ? value : 'text';

/** Form contents the AI produced, plus what to restore if the user undoes it. */
interface AIDraft {
  kind: 'created' | 'changed';
  summary?: string;
  previous: {
    fn: AIFunction;
    objectVersions: any[];
    selectedObjectVersion: string | null;
    formRevealed: boolean;
  };
}

const fieldShape = (fields: any[] = []) =>
  fields
    .map((f) => `${f.name || f.field_name}:${String(f.field_type || f.type || '').toLowerCase()}`)
    .sort()
    .join('|');

export const AIFunctionBuilder: React.FC = () => {
  const { user, permissions, isPublicMode, getEffectiveCustomerTag, hasActiveSubscription, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [aiFunction, setAIFunction] = useState<AIFunction>({
    name: '',
    display_name: '',
    description: '',
    instructions: '',
    input_types: ['text'],
    allowed_models: [...DEFAULT_ALLOWED_MODELS],
    response_type: 'text',
    is_list_response: false,
  });

  const [customObjects, setCustomObjects] = useState<CustomObject[]>([]);
  const [customObjectsLoaded, setCustomObjectsLoaded] = useState(false);
  const [savedFunctions, setSavedFunctions] = useState<AIFunction[]>([]);
  const [objectVersions, setObjectVersions] = useState<any[]>([]);
  const [selectedObjectVersion, setSelectedObjectVersion] = useState<string | null>(null);
  const [showObjectBuilder, setShowObjectBuilder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [examples, setExamples] = useState<any[]>([]);
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [selectedFunctionName, setSelectedFunctionName] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ open: false, message: '', severity: 'success' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [testResult, setTestResult] = useState<any>(null);
  const [functionVersions, setFunctionVersions] = useState<AIFunction[]>([]);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [testMaterials, setTestMaterials] = useState<TestMaterial[]>([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  // `?prompt=` / `?input=` (from the Build Guide and dashboard) prefill the create-with-AI box.
  const [searchParams, setSearchParams] = useSearchParams();
  const [savedNotice, setSavedNotice] = useState<{ isNew: boolean; id: string; name: string; version?: number } | null>(null);
  const urlPrompt = (searchParams.get('prompt') || '').slice(0, 4000);
  const urlInputType = searchParams.get('input');
  // `?platform=1` (admins, from Platform Routes): a new function is also served as a platform route.
  const platformMode = searchParams.get('platform') === '1' && !!permissions?.isAdmin;
  const [platformDraft, setPlatformDraft] = useState<RouteDraft>({ slug: searchParams.get('slug') || '', kind: 'text', status: 'testing' });
  const [aiPrompt, setAiPrompt] = useState(urlPrompt);
  const [aiInputType, setAiInputType] = useState(() => toInputType(urlInputType));
  useEffect(() => {
    if (urlPrompt) setAiPrompt(urlPrompt);
    if (urlInputType) setAiInputType(toInputType(urlInputType));
  }, [urlPrompt, urlInputType]);
  const [heroOpen, setHeroOpen] = useState(true);
  // New functions keep the form out of the way until AI fills it or the user chooses to fill it in.
  const [formRevealed, setFormRevealed] = useState(false);
  const [changeRequest, setChangeRequest] = useState('');
  const [saveAsNewVersion, setSaveAsNewVersion] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiDraft, setAiDraft] = useState<AIDraft | null>(null);
  const changeInputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setAiDraft(null);
    setChangeRequest('');
    setSaveAsNewVersion(true);
    setAiError(null);
  }, [aiFunction.id]);
  const learningStatus = useLearningStatus();
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [referencedObjectsLoading, setReferencedObjectsLoading] = useState(false);
  const referencedObjectsLoadedRef = useRef<Set<string>>(new Set());

  // Metadata state for chatbot/conversation settings
  const [isChatbotMode, setIsChatbotMode] = useState(false);
  const [chatHistoryLength, setChatHistoryLength] = useState(30);
  const [knowledgeCategories, setKnowledgeCategories] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<Array<{id: string, name: string}>>([]);
  const [knowledgeChunks, setKnowledgeChunks] = useState(2);
  const [introMessage, setIntroMessage] = useState('');
  const [overrideTitle, setOverrideTitle] = useState('');
  const [allowAttachments, setAllowAttachments] = useState(true);
  const [showCreditCounter, setShowCreditCounter] = useState(true);

  // Collapse state for metadata sections
  const [conversationSettingsExpanded, setConversationSettingsExpanded] = useState(false);
  const [knowledgeSettingsExpanded, setKnowledgeSettingsExpanded] = useState(false);
  const [reliabilityExpanded, setReliabilityExpanded] = useState(false);
  const [reliabilityDesc, setReliabilityDesc] = useState<ReliabilityDescription | null>(null);
  const reliabilityTargetId = aiFunction.original_uuid || aiFunction.id;
  const reliabilityActAsPublic = isPublicMode && !!permissions?.isAdmin;

  useEffect(() => {
    setReliabilityDesc(null);
    if (!reliabilityTargetId) return;
    let cancelled = false;
    reliabilityApi(getIdToken, reliabilityActAsPublic)
      .get('function', reliabilityTargetId)
      .then((d) => !cancelled && setReliabilityDesc(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [reliabilityTargetId, reliabilityActAsPublic, getIdToken]);

  useEffect(() => {
    if (user) {
      loadCustomObjects();
      loadSavedFunctions();
      loadTestMaterials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load available categories for knowledge base selection
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const token = await user?.getIdToken();
        const customerId = getEffectiveCustomerTag();
        const res = await fetch(`/api/v2/customers/${customerId}/categories`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAvailableCategories(data.data?.items || data.results || data.data || []);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    if (user) {
      loadCategories();
    }
  }, [user, getEffectiveCustomerTag]);

  const scrollToId = (id: string, block: ScrollLogicalPosition = 'start') =>
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block }), 150);

  // `?improve=true` (e.g. "Change with AI" in the functions list) lands on the change bar.
  const focusChangeBar = () =>
    setTimeout(() => {
      document.getElementById('change-with-ai')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      changeInputRef.current?.focus();
    }, 300);

  // Define loadFunctionFromUrl outside the useEffect so it can be reused
  const loadFunctionFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const functionId = params.get('id');
      const shouldTest = params.get('test');
      const shouldImprove = params.get('improve');
      


      
      // Clear existing function versions when loading a new function
      setFunctionVersions([]);
      
      if (functionId) {
        // Find the function by ID in saved functions
        let functionToLoad = savedFunctions.find(f => f.id === functionId);

        
        // If not found in saved functions, fetch it directly
        if (!functionToLoad) {
          try {
            const idToken = await user?.getIdToken();
            const response = await fetch(`/api/v2/ai-functions/${functionId}`, {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              functionToLoad = data.data || data;

            }
          } catch (error) {

          }
        }
        
        if (functionToLoad) {
          await handleSelectFunction(functionToLoad);
          
          if (shouldImprove === 'true') {
            focusChangeBar();
          }
          // If test=true in URL, scroll to test section after a brief delay
          else if (shouldTest === 'true') {
            setTimeout(() => {
              const testSection = document.getElementById('test-function-section');
              if (testSection) {
                testSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 500);
          }
        } else if (!functionToLoad) {

          // If not found in the list or list is empty, fetch it directly
          try {
            const idToken = await user?.getIdToken();
            const response = await fetch(`/api/v2/ai-functions/${functionId}`, {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              const func = data.data || data;

              
              // Clear old state before loading new function
              setFunctionVersions([]);
              setSelectedFunctionId(null);
              
              await handleSelectFunction(func);
              
              // Load versions after selecting the function
              if (func.name) {
                await loadFunctionVersions(func.name);
              }
              
              if (shouldImprove === 'true') {
                focusChangeBar();
              }
              // If test=true in URL, scroll to test section after a brief delay
              else if (shouldTest === 'true') {
                setTimeout(() => {
                  const testSection = document.getElementById('test-function-section');
                  if (testSection) {
                    testSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }
            }
          } catch (error) {

          }
        }
      }
  };

  // Handle loading function from URL parameter on initial mount
  useEffect(() => {
    // Load if we have user auth and custom objects are loaded
    // Don't require savedFunctions to be loaded as we might be coming from View Function button
    if (user && customObjectsLoaded) {
      const params = new URLSearchParams(window.location.search);
      const functionId = params.get('id');
      const forceReload = params.get('reload');
      
      // If forceReload is set, reload saved functions first
      if (forceReload === 'true') {
        loadSavedFunctions().then(() => {
          if (functionId) {
            loadFunctionFromUrl();
          }
        });
      } else if (functionId) {
        loadFunctionFromUrl();
      }
      
      // Handle scrollTo parameter
      const scrollTo = params.get('scrollTo');
      if (scrollTo === 'version-selector') {
        // Delay to ensure the element is rendered
        setTimeout(() => {
          const element = document.getElementById('version-selector');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight effect
            element.style.backgroundColor = '#e3f2fd';
            setTimeout(() => {
              element.style.backgroundColor = '';
            }, 2000);
          }
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, customObjectsLoaded]);

  

  // Ensure selected return object fields are loaded so we can render Pydantic
  useEffect(() => {
    const loadSelectedObjectFields = async () => {
      if (!aiFunction.response_object_id) return;
      const obj = customObjects.find(o => o.id === aiFunction.response_object_id);
      if (obj && (!Array.isArray(obj.fields) || obj.fields.length === 0)) {
        const full = await fetchObjectDetails(obj.id);
        if (full?.fields) {
          setCustomObjects(prev => prev.map(o => o.id === obj.id ? { ...o, fields: full.fields } : o));
        }
      } else if (obj && obj.fields) {
        // Also load any referenced objects recursively
        const loadReferencedObjects = async (fields: any[]) => {
          const referencedObjectIds = fields
          .filter((f: any) => 
            (f.field_type === 'reference' && f.reference_object_id) ||
            (f.field_type === 'array' && f.array_item_type === 'reference' && f.reference_object_id)
          )
            .map((f: any) => f.reference_object_id)
            .filter(id => id); // Remove any null/undefined values
        
        for (const refId of referencedObjectIds) {
            let refObj = customObjects.find(o => o.id === refId);
            
            // If object not found in customObjects, try to fetch it
            if (!refObj) {
              const fullRef = await fetchObjectDetails(refId);
              if (fullRef) {
                setCustomObjects(prev => {
                  // Check if already exists to avoid duplicates
                  if (prev.some(o => o.id === refId)) {
                    return prev.map(o => o.id === refId ? fullRef : o);
                  }
                  return [...prev, fullRef];
                });
                refObj = fullRef;
              }
            } else if (!refObj.fields || refObj.fields.length === 0) {
              // Object exists but fields not loaded
            const fullRef = await fetchObjectDetails(refId);
            if (fullRef?.fields) {
              setCustomObjects(prev => prev.map(o => o.id === refId ? { ...o, fields: fullRef.fields } : o));
                refObj = { ...refObj, fields: fullRef.fields };
            }
          }
            
            // Recursively load referenced objects from this object's fields
            if (refObj?.fields && refObj.fields.length > 0) {
              await loadReferencedObjects(refObj.fields);
        }
          }
        };
        
        await loadReferencedObjects(obj.fields);
      }
    };
    loadSelectedObjectFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiFunction.response_object_id]);

  // Separate effect to ensure referenced objects' fields are loaded for rendering pydantic schema
  useEffect(() => {
    const ensureRefsLoaded = async () => {
      if (!aiFunction.response_object_id) {
        setReferencedObjectsLoading(false);
        return;
      }
      
      const obj = customObjects.find(o => o.id === aiFunction.response_object_id);
      if (!obj || !obj.fields) {
        setReferencedObjectsLoading(false);
        return;
      }
      
      // Collect all referenced object IDs recursively
      const collectRefIds = (fields: any[]): Set<string> => {
        const ids = new Set<string>();
        fields.forEach((f: any) => {
          if ((f.field_type === 'reference' || (f.field_type === 'array' && f.array_item_type === 'reference')) && f.reference_object_id) {
            ids.add(f.reference_object_id);
          }
          if (f.fields && Array.isArray(f.fields)) {
            collectRefIds(f.fields).forEach(id => ids.add(id));
          }
          if (f.nested_fields && Array.isArray(f.nested_fields)) {
            collectRefIds(f.nested_fields).forEach(id => ids.add(id));
          }
        });
        return ids;
      };
      
      const refIds = collectRefIds(obj.fields);
      
      // Find referenced objects that need fields loaded
      const toLoad: string[] = [];
      for (const refId of Array.from(refIds)) {
        const refObj = customObjects.find(o => o.id === refId);
        if (refObj && (!refObj.fields || refObj.fields.length === 0)) {
          toLoad.push(refId);
        } else if (!refObj) {
          // Referenced object not even in customObjects - need to fetch it
          toLoad.push(refId);
        }
      }
      
      // Load all missing fields in parallel
      if (toLoad.length > 0) {
        setReferencedObjectsLoading(true);
        const loaded = await Promise.all(toLoad.map(id => fetchObjectDetails(id)));
        setCustomObjects(prev => {
          let updated = [...prev];
          for (const full of loaded) {
            if (!full) continue;
            const existing = updated.findIndex(o => o.id === full.id);
            if (existing >= 0) {
              // Update existing with loaded fields
              updated[existing] = { ...updated[existing], fields: full.fields };
            } else {
              // Add new referenced object
              updated.push(full);
            }
          }
          return updated;
        });
        setReferencedObjectsLoading(false);
      } else {
        setReferencedObjectsLoading(false);
      }
    };
    ensureRefsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencedObjectsLoading, aiFunction.response_object_id]);

  const fetchObjectDetails = async (objectId: string) => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const response = await fetchWithPublicMode(
        `/api/v2/custom-objects/${objectId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      if (!response.ok) {

        return null;
      }
      const data = await response.json();
      const full = data?.results || data?.data || data?.item || data;
      
      // If fields are not included or empty, fetch them separately
      if (full && (!full.fields || full.fields.length === 0)) {
        const fieldsResponse = await fetchWithPublicMode(
          `/api/v2/custom-objects/${objectId}/fields`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          },
          isPublicMode,
          isAdmin
        );
        
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          full.fields = fieldsData?.data || fieldsData?.results || [];
        }
      }
      
      return full;
    } catch (e) {

      return null;
    }
  };

  // Valid input types from backend
  const VALID_INPUT_TYPES = [
    'text', 'json', 'image', 'video', 'audio', 'mp3', 
    'pdf', 'csv', 'xml', 'html', 'markdown', 'binary'
  ];

  // Map common invalid input types to valid ones
  const mapInputType = (inputType: string): string => {
    const lowerType = inputType.toLowerCase();
    
    // Map common variations
    const typeMapping: Record<string, string> = {
      'file': 'binary',
      'files': 'binary',
      'document': 'pdf',
      'documents': 'pdf',
      'doc': 'pdf',
      'docx': 'pdf',
      'spreadsheet': 'csv',
      'excel': 'csv',
      'xls': 'csv',
      'xlsx': 'csv',
      'picture': 'image',
      'photo': 'image',
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image',
      'gif': 'image',
      'movie': 'video',
      'mp4': 'video',
      'avi': 'video',
      'sound': 'audio',
      'music': 'audio',
      'wav': 'audio',
      'webpage': 'html',
      'website': 'html',
      'markup': 'markdown',
      'md': 'markdown'
    };
    
    // Check if it's already valid
    if (VALID_INPUT_TYPES.includes(lowerType)) {
      return lowerType;
    }
    
    // Check mapping
    if (typeMapping[lowerType]) {
      return typeMapping[lowerType];
    }
    
    // Default to binary for unknown types
    return 'binary';
  };

  // Validate and fix input types array
  const validateInputTypes = (inputTypes: string[] | undefined): string[] => {
    if (!inputTypes || !Array.isArray(inputTypes) || inputTypes.length === 0) {
      return ['text'];
    }
    
    return inputTypes.map(type => mapInputType(type));
  };

  const fetchObjectVersions = async (objectId: string) => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const response = await fetchWithPublicMode(
        `/api/v2/custom-objects/${objectId}/versions`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      if (!response.ok) {

        return [];
      }
      const data = await response.json();
      const versions = data?.results || data?.data || [];
      // Sort versions by version number descending
      return versions.sort((a: any, b: any) => (b.version || 0) - (a.version || 0));
    } catch (e) {

      return [];
    }
  };

  const fetchFunctionDetails = async (identifier: string) => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const response = await fetchWithPublicMode(
        `/api/v2/ai-functions/${identifier}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      if (!response.ok) throw new Error('Failed to fetch function');
      const data = await response.json();
      const result = data?.results || data?.data || data?.item || data;

      return result;
    } catch (e) {

      return null;
    }
  };

  const loadCustomObjects = async () => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      // In public mode, only load public objects
      // Otherwise, load both user and public objects to ensure references work
      const params = new URLSearchParams({
        page_size: '100',
        ...getPublicModeParams(isPublicMode, isAdmin),
        // Always include public objects for reference resolution
        ...(isPublicMode ? {} : { include_public: 'true' })
      });
      
      const response = await fetchWithPublicMode(
        `/api/v2/custom-objects/?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      const data = await response.json();
      // Handle both 'results' and 'items' response formats
      const objects = data.results || data.items || [];
      
      // Load fields for important public objects like AIObjectReturnWrapper, AIFunctionReturn, and objectfields
      const publicObjectsToLoadFields = [
        'f1000000-0000-4000-b000-000000000001', // AIObjectReturnWrapper
        'f1000000-0000-4000-b000-000000000002', // AIFunctionReturn
        'fa661179-3910-4d99-b30e-0da11c9ac9e9'  // objectfields
      ];
      const objectsWithFields = await Promise.all(objects.map(async (obj: any) => {
        if (publicObjectsToLoadFields.includes(obj.id) && (!obj.fields || obj.fields.length === 0)) {
          const fullObj = await fetchObjectDetails(obj.id);
          return fullObj || obj;
        }
        return obj;
      }));
      
      // After loading all objects, ensure all referenced objects are loaded for public objects
      const finalObjects = [...objectsWithFields];
      const loadedIds = new Set(finalObjects.map(o => o.id));
      
      // Check each loaded object for references that need to be loaded
      for (const obj of objectsWithFields) {
        if (obj.fields && obj.fields.length > 0) {
          for (const field of obj.fields) {
            // Check if this field references an object that's not loaded
            if ((field.field_type === 'reference' || (field.field_type === 'array' && field.array_item_type === 'reference')) 
                && field.reference_object_id 
                && !loadedIds.has(field.reference_object_id)) {
              // Try to fetch the missing referenced object
              try {
                const refObj = await fetchObjectDetails(field.reference_object_id);
                if (refObj) {
                  finalObjects.push(refObj);
                  loadedIds.add(refObj.id);
                }
              } catch (err) {

              }
            }
          }
        }
      }
      
      setCustomObjects(finalObjects);
      setCustomObjectsLoaded(true);
      return finalObjects;
    } catch (error) {

      setCustomObjectsLoaded(true);
      return [];
    }
  };

  const loadSavedFunctions = async () => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      // Get appropriate parameters based on public mode
      const params = new URLSearchParams(getPublicModeParams(isPublicMode, isAdmin));
      
      const response = await fetchWithPublicMode(
        `/api/v2/ai-functions/?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      const data = await response.json();
      // The response format may be items or results
      const items = (data && Array.isArray(data.items)) ? data.items : (data?.results || []);
      setSavedFunctions(items);
    } catch (error) {

      setSavedFunctions([]);
    }
  };

  const loadTestMaterials = async () => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const response = await fetchWithPublicMode(
        '/api/v2/test-materials/',
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        },
        isPublicMode,
        isAdmin
      );

      if (response.ok) {
        const data = await response.json();

        setTestMaterials(data.results || data.data || []);
      }
    } catch (error) {

    }
  };

  const loadFunctionVersions = async (functionName: string) => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const response = await fetchWithPublicMode(
        `/api/v2/ai-functions/${functionName}/versions`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      
      if (response.ok) {
        const data = await response.json();
        const versions = data.results || data.versions || data || [];

        setFunctionVersions(versions);
      } else {

      }
    } catch (error) {

      setFunctionVersions([]);
    }
  };


  const handleSelectFunction = async (func: AIFunction) => {
    const identifier = func?.id || func?.name;
    setSavedNotice(null);

    const full = await fetchFunctionDetails(identifier);
    const loaded = full || func;

    
    // Extract response configuration from response_format or metadata
    let responseType: AIFunction['response_type'] = 'text';
    let responseObjectId: string | null = null;
    let isListResponse = false;
    
    // Parse response_format - it might be a string that needs parsing
    let responseFormat = loaded.response_format;
    if (typeof responseFormat === 'string' && responseFormat !== 'null') {
      try {
        responseFormat = JSON.parse(responseFormat);
      } catch (e) {
        // ignore parse errors
      }
    }
    
    if (responseFormat && responseFormat !== 'null') {
      if (responseFormat.type === 'array' || responseFormat.is_list) {
        responseType = 'list';
        isListResponse = true;
      } else if (responseFormat.type === 'object') {
        responseType = 'object';
      } else if (responseFormat.type === 'image') {
        responseType = 'image';
      } else if (responseFormat.type === 'video') {
        responseType = 'video';
      } else if (responseFormat.type === 'audio') {
        responseType = 'audio';
      }
      responseObjectId = responseFormat.object_id || null;
    }
    
    // Also check metadata if response_format didn't have the info
    let metadata = loaded.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        // ignore parse errors
      }
    }
    
    if (metadata) {
      if (metadata.response_type) {
        responseType = metadata.response_type as AIFunction['response_type'];
      }
      if (metadata.response_object_id) {
        responseObjectId = metadata.response_object_id;
      }
      if (metadata.is_list_response !== undefined) {
        isListResponse = metadata.is_list_response;
        if (isListResponse) {
          responseType = 'list';
        }
      }
      // Extract chatbot metadata (check both "chatbot" and "is_chatbot" keys)
      if (metadata.chatbot !== undefined || metadata.is_chatbot !== undefined) {
        const chatbotValue = metadata.is_chatbot !== undefined ? metadata.is_chatbot : metadata.chatbot;
        setIsChatbotMode(chatbotValue);
        // Keep collapsed by default - user can expand if needed
      }
      if (metadata.chat_history_length !== undefined) {
        setChatHistoryLength(metadata.chat_history_length);
      }
      if (metadata.knowledge_categories && Array.isArray(metadata.knowledge_categories)) {
        setKnowledgeCategories(metadata.knowledge_categories);
        // Keep collapsed by default - user can expand if needed
      }
      if (metadata.knowledge_chunks !== undefined) {
        setKnowledgeChunks(metadata.knowledge_chunks);
      }
      if (metadata.intro_message !== undefined) {
        setIntroMessage(metadata.intro_message);
      }
      if (metadata.override_title !== undefined) {
        setOverrideTitle(metadata.override_title);
      }
      if (metadata.allow_attachments !== undefined) {
        setAllowAttachments(metadata.allow_attachments);
      }
      if (metadata.show_credit_counter !== undefined) {
        setShowCreditCounter(metadata.show_credit_counter);
      }
    }
    


    
    // Additional debug logging
    if (responseObjectId) {


    }
    
    setSelectedFunctionName(loaded?.name || null);
    
    // Set all fields including display_name and original_uuid
    setAIFunction({
      id: loaded.id,
      original_uuid: loaded.original_uuid,
      name: loaded.name,
      display_name: loaded.display_name || loaded.name,
      description: loaded.description || '',
      instructions: loaded.instructions || '',
      input_types: validateInputTypes(loaded.input_types || ['text']),
      allowed_models: loaded.allowed_models || DEFAULT_ALLOWED_MODELS,
      response_type: responseType,
      response_object_id: responseObjectId,
      is_list_response: isListResponse,
      is_public: loaded.is_public || false,
      version: loaded.version,
      response_format: loaded.response_format,
      metadata: metadata,
    });
    setSelectedFunctionId(loaded.id || loaded.name);
    
    // If we have a response object, set the selected version and load all versions
    if (responseObjectId) {
      setSelectedObjectVersion(responseObjectId);
      
      // Find the object in customObjects
      let currentObject = customObjects.find(obj => obj.id === responseObjectId);
      
      // If object not found in customObjects, fetch it directly
      if (!currentObject) {

        const fetchedObject = await fetchObjectDetails(responseObjectId);
        if (fetchedObject) {
          // Add the fetched object to customObjects
          const newObject: CustomObject = {
            id: fetchedObject.id,
            name: fetchedObject.name,
            display_name: fetchedObject.display_name || fetchedObject.name,
            description: fetchedObject.description,
            fields: fetchedObject.fields,
            field_count: fetchedObject.fields?.length || 0
          };
          setCustomObjects(prev => [...prev, newObject]);
          currentObject = newObject;
        }
      }
      
      // Fetch all versions for this object
      if (currentObject) {
        const versions = await fetchObjectVersions(currentObject.id);
        setObjectVersions(versions);
      } else {
        setObjectVersions([]);
      }
    }
    
    // Load all versions for this function
    if (loaded.name) {
      await loadFunctionVersions(loaded.name);
    }
    if (loaded.id) {
      await loadTestMaterials();
    }
  };

  const generateExamples = async () => {
    if (!aiFunction.display_name) return;
    
    setLoading(true);
    try {
      // First save the function to get an ID
      const idToken = await user?.getIdToken();
      const saveResponse = await fetch('/api/v2/ai-functions/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          ...aiFunction,
          name: aiFunction.display_name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        }),
      });

      if (saveResponse.ok) {
        const savedFunction = await saveResponse.json();
        
        // Generate examples
        const examplesResponse = await fetch(`/api/v2/ai-functions/${savedFunction.data.id}/generate-examples`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ count: 3 }),
        });

        if (examplesResponse.ok) {
          const examplesData = await examplesResponse.json();
          setExamples(examplesData.data.examples);
        }
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleResponseTypeChange = (type: string) => {
    if (type === 'text' || type === 'image' || type === 'video' || type === 'audio') {
      setAIFunction({
        ...aiFunction,
        response_type: type as AIFunction['response_type'],
        is_list_response: false,
        response_object_id: null,
        response_format: { type },
      });
    } else {
      let updatedFormat = aiFunction.response_format;
      if (aiFunction.response_object_id && updatedFormat) {
        updatedFormat = {
          ...updatedFormat,
          type: type === 'list' ? 'array' : 'object',
          is_list: type === 'list'
        };
      }
      setAIFunction({
        ...aiFunction,
        response_type: type as AIFunction['response_type'],
        is_list_response: type === 'list',
        response_format: updatedFormat,
      });
    }
  };

  const isMediaResponseType = (type: string) => ['text', 'image', 'video', 'audio'].includes(type);

  const getSelectedResponseObject = (): CustomObject | undefined =>
    customObjects.find(obj => obj.id === aiFunction.response_object_id) ||
    objectVersions.find(v => v.id === aiFunction.response_object_id);

  const getResponseSchema = (): JsonSchema | null => {
    if (isMediaResponseType(aiFunction.response_type)) {
      return null;
    }
    const selectedObject = getSelectedResponseObject();
    if (!selectedObject || !Array.isArray(selectedObject.fields) || selectedObject.fields.length === 0) return null;
    return objectToJsonSchema(selectedObject, [...customObjects, ...objectVersions]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearFunction = () => {
    setAIFunction({
      id: undefined,
      original_uuid: undefined,
      name: '',
      display_name: '',
      description: '',
      instructions: '',
      input_types: ['text'],
      allowed_models: [...DEFAULT_ALLOWED_MODELS],
      response_type: 'text',
      is_list_response: false,
      version: undefined,
    });
    setSelectedFunctionId(null);
    setSelectedFunctionName(null);
    setExamples([]);
    setTestResult(null);
    setFunctionVersions([]);
    setTestMaterials([]);
  };

  /**
   * Asks AI to design a new function ('created') or change the one in the form ('changed') and
   * puts the result in the form for review. Only a new output object is saved straight away,
   * because the form can only point at saved objects.
   */
  const applyAIDraft = async (kind: AIDraft['kind']) => {
    const request = (kind === 'created' ? aiPrompt : changeRequest).trim();
    if (!hasActiveSubscription || !request || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    setSavedNotice(null);
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) throw new Error('Your session has expired. Please sign in again.');

      const currentObject =
        kind === 'changed' && !isMediaResponseType(aiFunction.response_type) ? getSelectedResponseObject() : undefined;
      const currentFields = Array.isArray(currentObject?.fields) ? currentObject!.fields! : [];
      const inputLabel = inputTypeOptions.find((t) => t.value === aiInputType)?.label || aiInputType;
      const compiled =
        kind === 'created'
          ? {
              function: { name: '', display_name: '', instructions: '', input_types: [aiInputType], allowed_models: aiFunction.allowed_models },
              metadata: { current_version: 1 },
              initialPrompt: `It takes in: ${inputLabel}\n\n${request}`,
            }
          : compileFunctionDataForImprovement(
              { ...aiFunction, name: aiFunction.name || 'draft_function' },
              currentObject && currentFields.length ? { ...currentObject, fields: currentFields } : undefined
            );
      const draft = await improveWithAI(compiled, idToken, request);

      let output: Partial<AIFunction> = {};
      const objectDef = draft.return_object;
      if (objectDef?.fields?.length) {
        const isList = draft.response_type === 'list';
        let objectId: string;
        let objectName: string | undefined;
        if (currentObject && fieldShape(currentFields) === fieldShape(objectDef.fields)) {
          objectId = currentObject.id;
          objectName = currentObject.name;
        } else {
          const saved = await saveGeneratedReturnObject(objectDef, idToken);
          objectId = saved.id;
          objectName = saved.name || objectDef.name;
          const full = await fetchObjectDetails(saved.id);
          if (full) {
            setCustomObjects((prev) =>
              prev.some((o) => o.id === full.id) ? prev.map((o) => (o.id === full.id ? { ...o, ...full } : o)) : [...prev, full]
            );
          }
          setSelectedObjectVersion(objectId);
          setObjectVersions(await fetchObjectVersions(objectId));
        }
        output = {
          response_type: isList ? 'list' : 'object',
          is_list_response: isList,
          response_object_id: objectId,
          response_format: { type: isList ? 'array' : 'object', object_id: objectId, object_name: objectName, is_list: isList },
        };
      } else if (kind === 'created') {
        output = { response_type: 'text', is_list_response: false, response_object_id: null, response_format: { type: 'text' } };
      }

      const suggestedInputs = Array.isArray(draft.input_types)
        ? draft.input_types
        : typeof draft.input_types === 'string'
          ? [draft.input_types]
          : [];
      // A change may add input types but never silently drops ones the function already takes.
      const inputTypes =
        kind === 'created'
          ? [aiInputType]
          : Array.from(new Set([...aiFunction.input_types, ...(suggestedInputs.length ? validateInputTypes(suggestedInputs) : [])]));
      const aiName = draft.function_name || draft.name || '';
      const displayName =
        draft.display_name ||
        (kind === 'changed' ? aiFunction.display_name : '') ||
        aiName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

      const previous = { fn: aiFunction, objectVersions, selectedObjectVersion, formRevealed };
      setAIFunction((prev) => ({
        ...prev,
        name: kind === 'changed' ? prev.name : aiName || 'new_function',
        display_name: displayName,
        description: draft.description || prev.description,
        instructions: draft.instructions || prev.instructions,
        input_types: inputTypes,
        ...output,
        metadata: {
          ...(prev.metadata || {}),
          ai_generated: true,
          ...(kind === 'created' ? { generation_prompt: request } : {}),
          ...(draft.enable_google_search ? { enable_google_search: true } : {}),
        },
      }));
      if (kind === 'changed') setChangeRequest('');
      setAiDraft({ kind, summary: draft.improvements_summary || undefined, previous });
      setFormRevealed(true);
      scrollToId('ai-draft-review');
    } catch (e) {
      const detail = e instanceof Error && e.message ? ` (${e.message})` : '';
      setAiError(`AI couldn't finish this one. Please try again, or rephrase what you want.${detail}`);
    } finally {
      setAiBusy(false);
    }
  };

  const undoAIDraft = () => {
    if (!aiDraft) return;
    const { kind, previous } = aiDraft;
    setAIFunction(previous.fn);
    setObjectVersions(previous.objectVersions);
    setSelectedObjectVersion(previous.selectedObjectVersion);
    setFormRevealed(previous.formRevealed);
    setAiDraft(null);
    if (kind === 'created') setHeroOpen(true);
  };

  const handleGenerateInstructions = async () => {
    if (!aiFunction.description) return;
    
    setGenerateLoading(true);
    try {
      const idToken = await user?.getIdToken();
      
      // Prepare input for the predictsysteminstructions function
      let input = `Function Description: ${aiFunction.description}\n\n`;
      
      // Add current instructions if any
      if (aiFunction.instructions) {
        input += `Current Instructions: ${aiFunction.instructions}\n\n`;
      }
      
      
      // Add response format info
      if (!isMediaResponseType(aiFunction.response_type)) {
        const responseObj = customObjects.find(obj => obj.id === aiFunction.response_object_id);
        if (responseObj) {
          input += `Expected Response: ${aiFunction.response_type === 'list' ? 'List of ' : ''}${responseObj.display_name} objects\n`;
        }
      }
      
      const response = await fetch('/api/v2/functions/public/predictsysteminstructions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: input,
          input_type: 'text',
        }),
      });

      if (response.ok) {
      const result = await response.json();
        if (result.result) {
          setAIFunction({ ...aiFunction, instructions: result.result });
          setSnackbar({ open: true, message: 'AI suggestions applied!', severity: 'success' });
        }
      } else {
        throw new Error('Failed to generate instructions');
      }
    } catch (error) {

      setSnackbar({ open: true, message: 'Failed to generate AI suggestions', severity: 'error' });
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSaveFunction = async () => {
    setLoading(true);
    try {
      const idToken = await user?.getIdToken();
      
      // Build response format based on selected object and type
      let responseFormat = null;
      if (!isMediaResponseType(aiFunction.response_type) && aiFunction.response_object_id) {
        const selectedObject = customObjects.find(obj => obj.id === aiFunction.response_object_id);
        if (selectedObject) {
          responseFormat = {
            type: aiFunction.response_type === 'list' ? 'array' : 'object',
            object_id: aiFunction.response_object_id,
            object_name: selectedObject.name,
            is_list: aiFunction.response_type === 'list'
          };
        }
      }

      // Determine if this is a new function or an update (unticking "new version" saves a separate copy)
      const isUpdate = !!(aiFunction.id && selectedFunctionId) && saveAsNewVersion;
      const newPlatformRoute = platformMode && !isUpdate && !slugProblem(platformDraft.slug, null);
      const cleanedName = newPlatformRoute ? functionNameFor(platformDraft.slug) : aiFunction.display_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      
      const payload: any = {
        name: aiFunction.name || cleanedName,  // Use existing name if updating, cleaned name if creating
        display_name: aiFunction.display_name,
        description: aiFunction.description,
        instructions: aiFunction.instructions,
        input_types: aiFunction.input_types,
        allowed_models: aiFunction.allowed_models,
        response_format: responseFormat,
        is_public: aiFunction.is_public || false,
        metadata: {
          // IMPORTANT: Preserve ALL existing metadata
          ...(aiFunction.metadata || {}),
          // Then overlay the fields we're managing in this UI
          response_type: aiFunction.response_type,
          response_object_id: aiFunction.response_object_id,
          is_list_response: aiFunction.is_list_response,
          // Chatbot/conversation metadata (save to both keys for compatibility)
          chatbot: isChatbotMode,
          is_chatbot: isChatbotMode,
          chat_history_length: chatHistoryLength,
          knowledge_categories: knowledgeCategories,
          knowledge_chunks: knowledgeChunks,
          intro_message: introMessage,
          override_title: overrideTitle,
          allow_attachments: allowAttachments,
          show_credit_counter: showCreditCounter,
        },
      };

      let response;
      if (isUpdate) {
        // Update existing function (creates new version)
        response = await fetchWithPublicMode(`/api/v2/ai-functions/${aiFunction.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(payload),
        }, isPublicMode, permissions?.isAdmin || false);
      } else {
        // Create new function with unique name check
        let functionCreated = false;
        let attemptCount = 0;
        const maxAttempts = 50;
        const baseName = cleanedName;
        
        while (!functionCreated && attemptCount < maxAttempts) {
          const attemptName = attemptCount === 0 ? baseName : `${baseName}_${attemptCount + 1}`;
          payload.name = attemptName;
          
          response = await fetchWithPublicMode('/api/v2/ai-functions/', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify(payload),
          }, isPublicMode, permissions?.isAdmin || false);
          
          if (response.ok) {
            functionCreated = true;
          } else {
            const error = await response.text();
            // Check if it's a name conflict error
            if (response.status === 400 && (error.includes('already exists') || error.includes('duplicate'))) {
              attemptCount++;
              continue;
            } else {
              // Other error, throw
              throw new Error(`Failed to create function: ${error}`);
            }
          }
        }
        
        if (!functionCreated) {
          throw new Error('Failed to create function: unable to find unique name');
        }
      }

      if (!response || !response.ok) throw new Error('Failed to save function');
      const savedData = await response.json();

      
      const savedFunction = savedData?.results || savedData?.data || savedData?.item || savedData;
      if (savedFunction?.id) {
        setSavedNotice({
          isNew: !isUpdate,
          id: savedFunction.id,
          name: savedFunction.display_name || aiFunction.display_name,
          version: savedFunction.version,
        });
        setSearchParams(platformMode ? { id: savedFunction.id, platform: '1' } : { id: savedFunction.id }, { replace: true });
        scrollToId('saved-next-steps', 'center');
        if (newPlatformRoute) {
          try {
            const setup = await platformRoutesApi(getIdToken).setup(savedFunction.id, platformDraft);
            setSnackbar({ open: true, message: `Serving ${setup.route.path} (${platformDraft.status}); platform defaults applied`, severity: 'success' });
          } catch (err) {
            setSnackbar({ open: true, message: `Saved, but the route wasn't set up: ${err instanceof Error ? err.message : String(err)}`, severity: 'warning' });
          }
        }
      } else {
        setSnackbar({ open: true, message: isUpdate ? 'Function saved as a new version' : 'New function created', severity: 'success' });
      }
      
      // Update the current function with the saved data
      if (savedFunction) {
        setAIFunction(prev => ({ 
          ...prev, 
          id: savedFunction.id, 
          name: savedFunction.name,  // Important: Update the name field!
          version: savedFunction.version,
          original_uuid: savedFunction.original_uuid 
        }));
        setSelectedFunctionId(savedFunction.name); // Keep using name as that's what the existing code uses
        setSelectedFunctionName(savedFunction.name);
      }
      
      // Reload function list and versions
      await loadSavedFunctions();
      if (savedFunction && savedFunction.name) {
        await loadFunctionVersions(savedFunction.name);
      }
    } catch (e) {

      setSnackbar({ open: true, message: 'Failed to save function', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Minimal effect to detect when we have a response object selected and ensure its refs are loaded
  useEffect(() => {
    if (!aiFunction.response_object_id) return;
    
    const obj = customObjects.find(o => o.id === aiFunction.response_object_id);
    if (!obj || !obj.fields) return;
    
    // Check if any referenced objects are missing fields
    const collectRefIds = (fields: any[]): Set<string> => {
      const ids = new Set<string>();
      fields.forEach((f: any) => {
        if ((f.field_type === 'reference' || (f.field_type === 'array' && f.array_item_type === 'reference')) && f.reference_object_id) {
          ids.add(f.reference_object_id);
        }
      });
      return ids;
    };
    
    const refIds = collectRefIds(obj.fields);
    let hasMissing = false;
    
    for (const refId of Array.from(refIds)) {
      const refObj = customObjects.find(o => o.id === refId);
      if (!refObj || !refObj.fields || refObj.fields.length === 0) {
        hasMissing = true;
        break;
      }
    }
    
    // If we have missing referenced objects and we haven't already tried to load them,
    // trigger the loading process
    if (hasMissing && !referencedObjectsLoadedRef.current.has(aiFunction.response_object_id)) {
      referencedObjectsLoadedRef.current.add(aiFunction.response_object_id);
      setReferencedObjectsLoading(true);
    }
  }, [aiFunction.response_object_id, customObjects]);

  const isSaved = !!aiFunction.id;
  const showChangeBar = isSaved || !!aiDraft;
  const showHero = !showChangeBar && hasActiveSubscription && heroOpen;
  const showForm = showChangeBar || formRevealed || !hasActiveSubscription;
  const learningHref =
    learningStatus?.enabled && aiFunction.id && !aiFunction.is_public && !isPublicMode
      ? learningPath('function', aiFunction.id)
      : undefined;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">
            🤖 AI Function Builder
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Create AI-powered functions that understand your custom data structures
          </Typography>
        </Box>
        
        {/* Compact Function Selector - Top Right */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {savedFunctions.length > 0 && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 300, maxWidth: 400 }}
              options={Object.values(
                savedFunctions.reduce((acc, func) => {
                  if (!acc[func.name] || (func.version ?? 1) > (acc[func.name]?.version ?? 1)) {
                    acc[func.name] = func;
                  }
                  return acc;
                }, {} as Record<string, AIFunction>)
              )}
              getOptionLabel={(option) => option.display_name || option.name}
              value={savedFunctions.find(f => f.id === selectedFunctionId || f.name === selectedFunctionId) || null}
              onChange={(_, value) => {
                if (value) {
                  handleSelectFunction(value);
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.display_name || option.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description?.substring(0, 50)}...
                      {(option.version ?? 0) > 1 && ` • v${option.version}`}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Edit existing (${Object.keys(savedFunctions.reduce((acc, func) => {
                    if (!acc[func.name] || (func.version ?? 1) > (acc[func.name]?.version ?? 1)) {
                      acc[func.name] = func;
                    }
                    return acc;
                  }, {} as Record<string, AIFunction>)).length})`}
                  placeholder="Search functions..."
                  variant="outlined"
                />
              )}
            />
          )}
          {aiFunction.id && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/dashboard/function-runs/${aiFunction.id}`)}
              startIcon={<HistoryIcon />}
              size="small"
              disabled={!hasActiveSubscription}
              title={hasActiveSubscription ? 'Recent runs of this function (all versions), with inputs, outputs and errors' : 'Requires active subscription'}
            >
              Recent runs
            </Button>
          )}
          {aiFunction.id && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/dashboard/function-runs/${aiFunction.id}?tab=performance`)}
              startIcon={<ShowChartIcon />}
              size="small"
              disabled={!hasActiveSubscription}
              title={hasActiveSubscription ? 'Speed, outcomes, time limit and cost from this function’s runs' : 'Requires active subscription'}
            >
              Performance
            </Button>
          )}
        </Box>
      </Box>

      {showChangeBar ? (
        <ChangeWithAIBar
          value={changeRequest}
          onChange={setChangeRequest}
          onApply={() => applyAIDraft('changed')}
          busy={aiBusy}
          error={aiError}
          hasActiveSubscription={hasActiveSubscription}
          functionName={isSaved ? selectedFunctionName || aiFunction.name : null}
          saveAsNewVersion={saveAsNewVersion}
          onSaveAsNewVersionChange={setSaveAsNewVersion}
          learningHref={learningHref}
          inputRef={changeInputRef}
        />
      ) : showHero ? (
        <CreateWithAIHero
          prompt={aiPrompt}
          onPromptChange={setAiPrompt}
          inputType={aiInputType}
          onInputTypeChange={setAiInputType}
          onCreate={() => applyAIDraft('created')}
          busy={aiBusy}
          error={aiError}
          onSetUpYourself={() => {
            setHeroOpen(false);
            setFormRevealed(true);
          }}
        />
      ) : (
        <SetUpYourselfBar hasActiveSubscription={hasActiveSubscription} onUseAI={() => setHeroOpen(true)} />
      )}

      {aiDraft && (
        <DraftReviewNotice
          kind={aiDraft.kind}
          summary={aiDraft.summary}
          onGoToSave={() => scrollToId('function-save', 'center')}
          onUndo={undoAIDraft}
        />
      )}

      <Grid container spacing={3} id="function-form" sx={{ mt: 1, display: showForm ? undefined : 'none' }}>
        <Grid item xs={12} lg={8}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 4, 
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              }
            }}
          >
            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                ✨ Function Details
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Define the basic properties and behavior of your AI function
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Function Name"
                  placeholder="e.g., Extract Customer Info"
                  value={aiFunction.display_name}
                  onChange={(e) => setAIFunction({ ...aiFunction, display_name: e.target.value })}
                />
              </Grid>
              
              {selectedFunctionId && functionVersions.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Version</InputLabel>
                    <Select
                      value={aiFunction.version || 1}
                      onChange={(e) => {
                        const selectedVersion = functionVersions.find(v => v.version === e.target.value);
                        if (selectedVersion) {
                          handleSelectFunction(selectedVersion);
                        }
                      }}
                    >
                      {functionVersions.map(version => (
                        <MenuItem key={version.id} value={version.version}>
                          Version {version.version}
                          {version.version === Math.max(...functionVersions.map(v => v.version || 1)) && ' (Latest)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Input Types</InputLabel>
                  <Select
                    multiple
                    value={aiFunction.input_types}
                    onChange={(e) => setAIFunction({ ...aiFunction, input_types: e.target.value as string[] })}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={inputTypeOptions.find(opt => opt.value === value)?.label} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {inputTypeOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <span style={{ marginRight: 8 }}>{option.icon}</span>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  placeholder="What does this AI function do?"
                  value={aiFunction.description}
                  onChange={(e) => setAIFunction({ ...aiFunction, description: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* AI Instructions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                📝 AI Instructions
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={handleGenerateInstructions}
                size="small"
                disabled={!aiFunction.description || generateLoading || !hasActiveSubscription}
                title={!hasActiveSubscription ? "Requires active subscription" : ""}
              >
                {generateLoading ? 'Generating...' : 'AI Suggest'}
              </Button>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="System Instructions"
              placeholder="Tell the AI exactly what to do, step by step..."
              value={aiFunction.instructions}
              onChange={(e) => setAIFunction({ ...aiFunction, instructions: e.target.value })}
              helperText="Be specific about what information to extract and how to format it"
            />

            <Divider sx={{ my: 3 }} />

            {/* Response Configuration */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.dark' }}>
              🎯 Output Format
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Response Type</InputLabel>
                  <Select
                    value={aiFunction.response_type}
                    onChange={(e) => handleResponseTypeChange(e.target.value)}
                  >
                    <MenuItem value="text">Plain Text</MenuItem>
                    <MenuItem value="object">Custom Object</MenuItem>
                    <MenuItem value="list">List of Objects</MenuItem>
                    <MenuItem value="image">🎨 Image Generation</MenuItem>
                    <MenuItem value="video">🎬 Video Generation</MenuItem>
                    <MenuItem value="audio">🎤 Audio Transcription</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Image generation info */}
              {aiFunction.response_type === 'image' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>🎨 Image Generation Response:</strong> This function will generate images based on the input prompt.
                      Make sure to select image generation models (like DALL-E, GPT Image, Imagen, or Nano Banana 2) in the AI Models section above.
                      Generated images will be returned as URLs or base64 data and displayed automatically in test results.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {aiFunction.response_type === 'video' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>🎬 Video Generation Response:</strong> This function will generate videos from a text prompt.
                      Make sure to select video generation models (like Veo 3.1, Veo 3.0, or Veo 2.0) in the AI Models section above.
                      Generated videos will be returned as URLs and displayed with a video player in test results. Videos are typically 4–8 seconds long.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {aiFunction.response_type === 'audio' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>🎤 Audio Transcription Response:</strong> This function will transcribe audio input to text.
                      Make sure to select an audio model (like Whisper Large V3) in the AI Models section above.
                      Send audio files as input, and the function will return the transcription as text.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {!isMediaResponseType(aiFunction.response_type) && (
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      options={[...customObjects].sort((a, b) => {
                        // Sort: My Objects first, then Public Objects, alphabetically within each group
                        const aIsPublic = a.customer_id === 'public';
                        const bIsPublic = b.customer_id === 'public';
                        if (aIsPublic !== bIsPublic) return aIsPublic ? 1 : -1;
                        return (a.display_name || a.name).localeCompare(b.display_name || b.name);
                      })}
                      getOptionLabel={(option) => option.customer_id === 'public' ? `${option.display_name} (Public)` : option.display_name}
                      groupBy={(option) => option.customer_id === 'public' ? '📦 Public Objects' : '📁 My Objects'}
                      value={customObjects.find(obj => obj.id === aiFunction.response_object_id) || 
                             objectVersions.find(v => v.id === aiFunction.response_object_id) || null}
                      ListboxProps={{
                        style: { maxHeight: 400 }
                      }}
                      componentsProps={{
                        popper: {
                          style: { width: 500 },
                          placement: 'bottom-start'
                        }
                      }}
                      onChange={async (_, value) => {
                        if (value) {
                          // Update both response_object_id and response_format
                          setAIFunction({ 
                            ...aiFunction, 
                            response_object_id: value.id || null,
                            response_format: {
                              type: aiFunction.response_type === 'list' ? 'array' : 'object',
                              object_id: value.id,
                              object_name: value.name,
                              is_list: aiFunction.response_type === 'list'
                            }
                          });
                          // Set selected version to the current object's ID (latest version)
                          setSelectedObjectVersion(value.id || null);
                          
                          // Fetch all versions for this object (skip for public objects)
                          if (value.customer_id !== 'public') {
                            const versions = await fetchObjectVersions(value.id!);
                            setObjectVersions(versions);
                          } else {
                            setObjectVersions([]);  // No versions for public objects
                          }
                          
                          // Optionally fetch fields to improve preview
                          const full = await fetchObjectDetails(value.id!);
                          if (full) {
                            setCustomObjects(prev => prev.map(o => o.id === value.id ? { ...o, fields: full.fields } : o));
                          }
                        } else {
                          // Clear selection
                          setAIFunction({ 
                            ...aiFunction, 
                            response_object_id: null,
                            response_format: undefined
                          });
                          setObjectVersions([]);
                          setSelectedObjectVersion(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Select Object Type" 
                          placeholder="Choose the object structure for responses"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">{option.display_name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {option.description || `${option.field_count ?? option.fields?.length ?? 0} fields`}
                            </Typography>
                          </Box>
                          {option.customer_id === 'public' && (
                            <Chip 
                              label="Public" 
                              size="small" 
                              color="info" 
                              variant="outlined"
                              sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      )}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<AddIcon />}
                      onClick={() => setShowObjectBuilder(true)}
                      sx={{ height: '56px', whiteSpace: 'nowrap' }}
                    >
                      New Object
                    </Button>
                  </Box>
                  
                  {/* Version selector for the selected object */}
                  {aiFunction.response_object_id && objectVersions.length > 0 && (
                    <Box id="version-selector" sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Object Version</InputLabel>
                        <Select
                          value={selectedObjectVersion || aiFunction.response_object_id}
                          onChange={(e) => {
                            const newVersionId = e.target.value as string;
                            setSelectedObjectVersion(newVersionId);
                            
                            // Update the response format with the new version ID
                            const selectedVersion = objectVersions.find(v => v.id === newVersionId);
                            if (selectedVersion) {
                              setAIFunction({
                                ...aiFunction,
                                response_object_id: newVersionId,
                                response_format: {
                                  type: aiFunction.response_type === 'list' ? 'array' : 'object',
                                  object_id: newVersionId,
                                  object_name: selectedVersion.name,
                                  is_list: aiFunction.response_type === 'list'
                                }
                              });
                              
                              // Fetch fields for the selected version to improve preview
                              fetchObjectDetails(newVersionId).then(full => {
                                if (full) {
                                  setCustomObjects(prev => {
                                    // Add the versioned object to customObjects if not already there
                                    const exists = prev.some(o => o.id === newVersionId);
                                    if (!exists) {
                                      return [...prev, full];
                                    }
                                    return prev.map(o => o.id === newVersionId ? { ...o, fields: full.fields } : o);
                                  });
                                }
                              });
                            }
                          }}
                          label="Object Version"
                        >
                          {objectVersions.map((version: any) => (
                            <MenuItem key={version.id} value={version.id}>
                              Version {version.version} {version.is_active && '(latest)'}
                              {!version.is_active && ' (archived)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>
              )}
              
              {aiFunction.response_type === 'list' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    The AI will return an array of {customObjects.find(obj => obj.id === aiFunction.response_object_id)?.display_name || 
                                                    objectVersions.find(v => v.id === aiFunction.response_object_id)?.display_name || 'objects'}
                  </Alert>
                </Grid>
              )}

              {/* Show Pydantic class for the selected return object */}
              {!isMediaResponseType(aiFunction.response_type) && aiFunction.response_object_id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Return Object (Pydantic)</Typography>
                  <CodeHighlight
                    language="python"
                    maxHeight={400}
                    code={(() => {
                        const obj = customObjects.find(o => o.id === aiFunction.response_object_id) || 
                                  objectVersions.find(v => v.id === aiFunction.response_object_id);
                          if (obj && Array.isArray(obj.fields) && obj.fields.length > 0) {
                          // Show loading state while referenced objects are being fetched
                          if (referencedObjectsLoading) {
                            return 'Loading nested object definitions...';
                          }
                          // First check if this is from AI response with dot notation
                          const objToFormat = obj.fields && obj.fields.some((f: any) => 
                            (f.field_name || f.name || '').includes('.')
                          ) ? transformObjectWithNestedFields(obj) : obj;
                          
                          // Collect all referenced object IDs recursively
                          const collectRefIds = (fields: any[]): Set<string> => {
                            const ids = new Set<string>();
                            fields.forEach((f: any) => {
                              if ((f.field_type === 'reference' || (f.field_type === 'array' && f.array_item_type === 'reference')) 
                                && f.reference_object_id) {
                                ids.add(f.reference_object_id);
                              }
                              if (f.fields && Array.isArray(f.fields)) {
                                collectRefIds(f.fields).forEach(id => ids.add(id));
                              }
                              if (f.nested_fields && Array.isArray(f.nested_fields)) {
                                collectRefIds(f.nested_fields).forEach(id => ids.add(id));
                              }
                            });
                            return ids;
                          };
                          
                          const refIds = collectRefIds(objToFormat.fields || []);
                          
                          // Ensure all referenced objects have their fields loaded
                          let objsForMapping = [...customObjects];
                          
                          // For each referenced object, check if it needs fields loaded
                          refIds.forEach((refId: string) => {
                            const refObj = objsForMapping.find(o => o.id === refId);
                            if (refObj && (!refObj.fields || refObj.fields.length === 0)) {
                              // Try to fetch fields for this referenced object
                              // This is async but we're in render, so we accept that the preview might be incomplete on first render
                              // The real loading happens in the useEffect that runs when response_object_id changes

                            }
                          });
                          
                          // Use customObjects which should have all objects (with fields loaded by the useEffect)
                          const pydanticLike = {
                            name: objToFormat.name,
                            display_name: objToFormat.display_name,
                            description: objToFormat.description,
                            fields: (objToFormat.fields || []).map((f: any) => mapFieldForPydantic(f, objsForMapping))
                          };
                          
                          return formatAsPydanticClass(pydanticLike as any);
                        }
                        return 'Loading object structure...';
                      })()}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Model Selection */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.dark' }}>
              🤖 AI Models
            </Typography>
            
            <ModelSelector
              multiple
              value={aiFunction.allowed_models}
              onChange={(val) => setAIFunction({ ...aiFunction, allowed_models: val as string[] })}
              label="Allowed Models"
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => setAIFunction({ ...aiFunction, allowed_models: modelOptions.map(o => o.value) })}
                sx={{ fontSize: '0.75rem' }}
              >
                Select All ({modelOptions.length})
              </Button>
              {aiFunction.allowed_models.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setAIFunction({ ...aiFunction, allowed_models: [] })}
                  startIcon={<ClearIcon />}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Clear All ({aiFunction.allowed_models.length})
                </Button>
              )}
            </Box>

            {/* Google Search grounding toggle — only show when Gemini models are selected */}
            {aiFunction.allowed_models.some(m => m.startsWith('gemini')) && (
              <FormControlLabel
                control={
                  <Switch
                    checked={aiFunction.metadata?.enable_google_search || false}
                    onChange={(e) => setAIFunction({
                      ...aiFunction,
                      metadata: { ...(aiFunction.metadata || {}), enable_google_search: e.target.checked }
                    })}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2">Enable Google Search</Typography>
                      <Chip label="Gemini only" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Let the AI search the web for current, real-time information. Billed per search query generated by the model.
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1, ml: 0 }}
              />
            )}
            
            {/* Show info alert if image or video generation models are selected */}
            {(() => {
              const hasImageModels = aiFunction.allowed_models.some(modelName => 
                modelOptions.find(opt => opt.value === modelName && opt.category === 'image')
              );
              const hasVideoModels = aiFunction.allowed_models.some(modelName => 
                modelOptions.find(opt => opt.value === modelName && opt.category === 'video')
              );
              const hasOnlyImageModels = aiFunction.allowed_models.length > 0 && 
                aiFunction.allowed_models.every(modelName => 
                  modelOptions.find(opt => opt.value === modelName && opt.category === 'image')
                );
              const hasOnlyVideoModels = aiFunction.allowed_models.length > 0 && 
                aiFunction.allowed_models.every(modelName => 
                  modelOptions.find(opt => opt.value === modelName && opt.category === 'video')
                );
              
              if (hasVideoModels) {
                return (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>🎥 Video Generation Mode:</strong> {hasOnlyVideoModels ? 'This function' : 'Some models'} will generate videos. 
                      Provide a text prompt describing the video (e.g., "A cat playing with a ball"). 
                      Videos will be up to {hasOnlyVideoModels && aiFunction.allowed_models[0] === 'veo-3.1' ? '8' : '12'} seconds, 1080p resolution.
                      Videos will be displayed automatically in the test interface and stored in GCS for 24 hours.
                    </Typography>
                  </Alert>
                );
              }
              
              if (hasImageModels) {
                return (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>🎨 Image Generation Mode:</strong> {hasOnlyImageModels ? 'This function' : 'Some models'} will generate images. 
                      Provide a text prompt as input, and the function will return generated image(s). 
                      Images will be displayed automatically in the test interface and stored in GCS for 24 hours.
                    </Typography>
                  </Alert>
                );
              }
              return null;
            })()}

            {/* Metadata Settings - Collapsible Sections */}
            
            {/* Conversation Settings Section */}
            <Accordion 
              expanded={conversationSettingsExpanded} 
              onChange={() => setConversationSettingsExpanded(!conversationSettingsExpanded)}
              sx={{ mt: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  💬 Chatbot Settings {isChatbotMode && <Chip label="ENABLED" size="small" sx={{ ml: 1 }} />}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChatbotMode}
                        onChange={(e) => setIsChatbotMode(e.target.checked)}
                      />
                    }
                    label="Enable chatbot mode with thread-based conversations"
                  />
                  
                  {isChatbotMode && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ml: 2 }}>
                      {/* Conversation Settings */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                          💬 Conversation Settings
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Chat History Length"
                            type="number"
                            value={chatHistoryLength}
                            onChange={(e) => setChatHistoryLength(parseInt(e.target.value) || 25)}
                            size="small"
                            fullWidth
                            inputProps={{ min: 5, max: 50 }}
                            helperText="Number of previous messages to include (5-50)"
                          />
                          
                          <TextField
                            fullWidth
                            label="Display Title Override (optional)"
                            value={overrideTitle}
                            onChange={(e) => setOverrideTitle(e.target.value)}
                            size="small"
                            placeholder="Leave empty to use function name"
                          />
                          
                          <TextField
                            fullWidth
                            label="Introduction Message (optional)"
                            value={introMessage}
                            onChange={(e) => setIntroMessage(e.target.value)}
                            size="small"
                            multiline
                            rows={2}
                            placeholder="First message shown to users"
                          />
                        </Box>
                      </Box>
                      
                      <Divider />
                      
                      {/* Display Options */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                          ⚙️ Display Options
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={allowAttachments}
                                onChange={(e) => setAllowAttachments(e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Allow file attachments</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showCreditCounter}
                                onChange={(e) => setShowCreditCounter(e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Show credits counter</Typography>}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Knowledge Categories Section */}
            <Accordion 
              expanded={knowledgeSettingsExpanded} 
              onChange={() => setKnowledgeSettingsExpanded(!knowledgeSettingsExpanded)}
              sx={{ mt: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  🔍 Knowledge Categories {knowledgeCategories.length > 0 && <Chip label={`${knowledgeCategories.length} selected`} size="small" sx={{ ml: 1 }} />}
                  {aiFunction.metadata?.vector_search?.enabled && <Chip label="Built-in knowledge on" color="info" size="small" sx={{ ml: 1 }} />}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {aiFunction.metadata?.vector_search?.enabled && (
                    <Alert severity="info">
                      <strong>Built-in knowledge:</strong>{' '}
                      {(aiFunction.metadata.vector_search.category_names || []).join(', ') || 'all categories'},{' '}
                      {aiFunction.metadata.vector_search.top_k ?? 8} snippets on every direct call, including API calls.
                      It's separate from the box below. Chain steps use their own knowledge settings instead.
                      {aiFunction.metadata.platform_tool && (
                        <> To change these examples, see <code>api/common/platform_tools/UPF_DOC.md</code> in
                          b2b-infrastructure; don't edit them in the Knowledge Hub.</>
                      )}
                    </Alert>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    Default categories for Chatbot Studio, the code examples and the batch runner. API calls search them only when the request includes <code>metadata.vector_search</code> (the code examples show how).
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={knowledgeCategories}
                      onChange={(e) => setKnowledgeCategories(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip 
                              key={value} 
                              label={availableCategories.find(c => c.id === value)?.name || value} 
                              size="small"
                              onDelete={() => setKnowledgeCategories(knowledgeCategories.filter(c => c !== value))}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {availableCategories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox checked={knowledgeCategories.includes(category.id)} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Knowledge Chunks - logically grouped with categories */}
                  {knowledgeCategories.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        label="Knowledge Chunks"
                        type="number"
                        value={knowledgeChunks}
                        onChange={(e) => setKnowledgeChunks(parseInt(e.target.value) || 2)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 1, max: 10 }}
                        helperText="Number of relevant chunks to retrieve from selected categories (1-10)"
                      />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Reliability Section */}
            <Accordion
              expanded={reliabilityExpanded}
              onChange={() => setReliabilityExpanded(!reliabilityExpanded)}
              sx={{ mt: 1 }}
              TransitionProps={{ unmountOnExit: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>🛡️ Reliability</Typography>
                  {reliabilityDesc && (
                    <Chip
                      size="small"
                      label={reliabilityDesc.is_standard ? 'Standard' : 'Custom'}
                      color={reliabilityDesc.is_standard ? 'default' : 'primary'}
                      variant="outlined"
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {!reliabilityTargetId
                      ? 'Time limit, retries and model fallback'
                      : reliabilityDesc && !reliabilityDesc.is_standard
                        ? summarizeReliability(reliabilityDesc.settings).join(' · ')
                        : 'Time limit, retries and model fallback'}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {reliabilityTargetId ? (
                  <ReliabilityPanel
                    targetType="function"
                    targetId={reliabilityTargetId}
                    actAsPublic={reliabilityActAsPublic}
                    onLoaded={setReliabilityDesc}
                    onSaved={setReliabilityDesc}
                  />
                ) : (
                  <Alert severity="info" variant="outlined">
                    Save the function first. Reliability settings belong to the saved function and carry over to every new version.
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {!aiFunction.is_public && (
              <FunctionLearningAccordion functionId={aiFunction.id} originalUuid={aiFunction.original_uuid} />
            )}

            {!!permissions?.isAdmin && !aiFunction.is_public && (platformMode || !!aiFunction.metadata?.platform_tool?.dynamic) && (
              <PlatformRouteAccordion functionId={aiFunction.id || undefined} draft={platformDraft} onDraftChange={setPlatformDraft} />
            )}

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowObjectBuilder(true)}
              >
                New Object
              </Button>
              <Button
                variant="outlined"
                startIcon={<TestIcon />}
                onClick={generateExamples}
                disabled={!aiFunction.display_name || loading || !hasActiveSubscription}
                title={!hasActiveSubscription ? "Requires active subscription" : ""}
              >
                Generate Examples
              </Button>
              <Button
                id="function-save"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!aiFunction.display_name || !aiFunction.instructions || loading}
                sx={{ backgroundColor: '#4caf50' }}
                onClick={handleSaveFunction}
              >
                {loading
                  ? 'Saving...'
                  : !isSaved
                    ? 'Save Function'
                    : saveAsNewVersion
                      ? 'Save as New Version'
                      : 'Save as Separate Copy'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Side Panel - Examples and Preview */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Schema Preview
            </Typography>
            
            {isMediaResponseType(aiFunction.response_type) ? (
              <Alert severity="info">
                {aiFunction.response_type === 'image' 
                  ? 'Response will be generated image(s) with URLs and metadata'
                  : aiFunction.response_type === 'video'
                  ? 'Response will be a generated video with URL and metadata'
                  : aiFunction.response_type === 'audio'
                  ? 'Response will be transcribed text from audio input'
                  : 'Response will be plain text without structured format'}
              </Alert>
            ) : !aiFunction.response_object_id ? (
              <Alert severity="info">
                Select a return object to preview the {aiFunction.response_type === 'list' ? 'list item' : 'response'} structure.
              </Alert>
            ) : (() => {
              const isList = aiFunction.response_type === 'list' || !!aiFunction.is_list_response;
              const selectedObject = getSelectedResponseObject();
              const label = selectedObject?.display_name || selectedObject?.name;
              return (
                <Box sx={{ mt: 1 }}>
                  <ObjectSchemaView
                    objectId={aiFunction.response_object_id}
                    fallbackSchema={getResponseSchema()}
                    isList={isList}
                    name={selectedObject?.name}
                    title={label ? `${isList ? 'List of ' : ''}${label}` : 'Response'}
                    maxHeight={420}
                  />
                </Box>
              );
            })()}

            {examples.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Examples
                </Typography>
                {examples.map((example, index) => (
                  <Card key={index} sx={{ mb: 2, backgroundColor: '#f9f9f9' }}>
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">
                        Example {index + 1}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Input:</strong> {example.input}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Expected Output:</strong> {example.expected_output}
                      </Typography>
                      {example.description && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          {example.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {savedNotice && savedNotice.id === aiFunction.id && (
        <SavedNextSteps
          id="saved-next-steps"
          sx={{ mt: 3 }}
          title={savedNotice.isNew ? `${savedNotice.name} is saved` : `Saved as version ${savedNotice.version ?? 'new'}`}
          message={
            !hasActiveSubscription
              ? 'Testing and code examples need a plan.'
              : savedNotice.isNew
                ? 'Next, try it with a real input below. When it works, connect it to your app with the code examples.'
                : 'Try the change with a real input below.'
          }
          actions={[
            ...(hasActiveSubscription
              ? [
                  { label: 'Test it', icon: <PlayArrowIcon />, primary: true, onClick: () => scrollToId('test-function-section') },
                  { label: 'Get code', icon: <CodeIcon />, onClick: () => navigate(`/dashboard/ai-functions/${savedNotice.id}/code`) },
                ]
              : [{ label: 'View plans', primary: true, onClick: () => navigate('/dashboard/subscription') }]),
            { label: 'Function Library', icon: <LibraryIcon />, onClick: () => navigate('/dashboard/ai-functions-list') },
          ]}
          onClose={() => setSavedNotice(null)}
        />
      )}

      {/* Enhanced Test Function Section */}
      {aiFunction.id && (
        <Paper 
          id="test-function-section"
          sx={{ 
            p: 4, 
            mt: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            border: '2px solid #e3e8ef',
            opacity: 1,
            position: 'relative',
            overflow: 'hidden',
            filter: hasActiveSubscription ? 'none' : 'saturate(0.3) brightness(0.92)',
            transition: 'filter 0.3s ease',
            '&:hover': {
              filter: hasActiveSubscription ? 'none' : 'saturate(0.4) brightness(0.96)',
            }
          }}
        >
          {!hasActiveSubscription && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(0,0,0,0.3), rgba(0,0,0,0.5))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backdropFilter: 'blur(1px)',
              cursor: 'pointer',
              '&:hover': {
                background: 'radial-gradient(circle at center, rgba(0,0,0,0.4), rgba(0,0,0,0.6))',
              }
            }}
            onClick={() => navigate('/dashboard/subscription')}
            >
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <LockIcon sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  🔒 Test Function Locked
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                  Subscribe to test your functions
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: 'white',
                    color: '#667eea',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    }
                  }}
                >
                  View Plans →
                </Button>
              </Box>
            </Box>
          )}
          
          <Box sx={{ position: 'relative', zIndex: hasActiveSubscription ? 'auto' : 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🧪 Test Your Function
                      </Typography>
                <Typography variant="body1" color="textSecondary">
                  {hasActiveSubscription 
                    ? 'Try your function with sample inputs or use pre-made test materials'
                    : 'Testing requires an active subscription'}
                </Typography>
                  </Box>
              <Chip 
                icon={<PlayArrowIcon />}
                label={hasActiveSubscription ? "Interactive Testing" : "Locked"}
                  color={hasActiveSubscription ? "primary" : "default"}
                  variant="outlined" 
              />
            </Box>
            
            {!hasActiveSubscription && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Subscription Required:</strong> Testing your functions requires an active subscription. Please upgrade to test your functions.
              </Alert>
            )}
            
            <Box sx={{ pointerEvents: hasActiveSubscription ? 'auto' : 'none', opacity: hasActiveSubscription ? 1 : 0.5 }}>
              <FunctionTester
                functionId={aiFunction.id}
                functionName={aiFunction.name}
                customerTag={getEffectiveCustomerTag()}
                inputTypes={aiFunction.input_types}
                allowedModels={aiFunction.allowed_models}
                testMaterials={testMaterials}
                onTestComplete={(result) => setTestResult(result)}
                functionMetadata={aiFunction.metadata}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {/* Object Builder Modal */}
      {showObjectBuilder && (
        <ObjectBuilderComponent
          isModal={true}
          onClose={() => {
            setShowObjectBuilder(false);
            loadCustomObjects(); // Reload objects when closing
          }}
          onSave={async (savedObject) => {

            // Reload custom objects
            await loadCustomObjects();
            // Auto-select the newly created object
            setAIFunction({ 
              ...aiFunction, 
              response_object_id: savedObject.id || null 
            });
            // Close the modal
            setShowObjectBuilder(false);
          }}
        />
      )}

      {/* Version History Dialog */}
      <Dialog
        open={showVersionDialog}
        onClose={() => setShowVersionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version History
          <IconButton
            onClick={() => setShowVersionDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {functionVersions.map((version) => (
              <Grid item xs={12} key={version.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                  onClick={() => {
                    handleSelectFunction(version);
                    setShowVersionDialog(false);
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6">
                          Version {version.version}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {version.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Updated: {new Date(version.updated_at ?? version.created_at ?? "").toLocaleString()}
                        </Typography>

                      </Box>
                      {version.version === aiFunction.version && (
                        <Chip label="Current" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 5000 : null}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setSnackbar({ ...snackbar, open: false });
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <UseCasesModal open={useCasesOpen} onClose={() => setUseCasesOpen(false)} />
    </Box>
  );
};
